import type { TFunction } from 'i18next';
import * as yup from 'yup';
import { ValidationError } from 'yup';

export type InvoicePayerEmailsInput = {
  payerEmails: string[];
};

export function buildInvoicePayerEmailsSchema(t: TFunction) {
  return yup.object().shape({
    payerEmails: yup.array().of(yup.string().trim().email(t('errors.email'))),
  });
}

/**
 * Validates payer email rows: trims, drops empty slots; each entered address must be valid (all empty is OK).
 * @throws {ValidationError} with i18n messages from `errors.email` or checkout keys
 */
export function validateInvoicePayerEmails(
  rows: string[],
  t: TFunction,
): string[] {
  const trimmed = rows.map((s) => s.trim()).filter(Boolean);
  const schema = buildInvoicePayerEmailsSchema(t);
  schema.validateSync({ payerEmails: trimmed });
  return trimmed;
}

const normalizeEmail = (email: string) => email.trim().toLowerCase();

export function buildPayerEmailsForApi(
  rows: string[],
  loggedInEmail: string | null | undefined,
  t: TFunction,
): string[] {
  const fromForm = validateInvoicePayerEmails(rows, t);
  const billTo = loggedInEmail?.trim();
  if (!billTo) return fromForm;
  const billNorm = normalizeEmail(billTo);
  if (fromForm.some((e) => normalizeEmail(e) === billNorm)) {
    return fromForm;
  }
  return [billTo, ...fromForm];
}

export function isYupValidationError(err: unknown): err is ValidationError {
  return ValidationError.isError(err);
}
